import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {apiInstance} from "../../api/api";
import {getNewViewLaunchUrl, MANUAL_VIEW_PARAM} from "../../utils/NewViewUtils";
import {ModalWithTitleAndButtons} from "../../components/modal";
import Button from "../../components/ui/Button";

// Temporary rollout feature (PFS-10283): asks first-time users whether V2 should be their
// default landing page, and otherwise redirects to whichever version the user has already
// chosen as their default. Skips the check entirely when the user arrived via an explicit
// manual view-switch link (MANUAL_VIEW_PARAM) -- that always wins over the stored default,
// so the "new view" preview link keeps working regardless of opt-in status. Safe to delete
// this whole component (and its mount point in App.tsx) once the beta opt-in rollout is
// complete.
const BetaOptInGuard: React.FC = () => {
	const {t} = useTranslation();
	const [isPromptOpen, setIsPromptOpen] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	// Captured once via lazy initializer (runs during the initial render, before any effect) --
	// MainRouter's catch-all <Redirect> strips the query string shortly after mount, so reading
	// window.location.search from inside the effect below would race it and lose the flag.
	const [isManualView] = useState(
		() => new URLSearchParams(window.location.search).has(MANUAL_VIEW_PARAM)
	);
	// Captured the same way, for the same reason: Premium's q=/j= launch params are only in
	// the URL during this first render. Resolving the redirect target now means an opted-in
	// user launched from a Premium quote or job lands on that project in the new view rather
	// than on its project list.
	const [newViewUrl] = useState(() => getNewViewLaunchUrl(window.location.search));

	useEffect(() => {
		if (isManualView) {
			return;
		}
		apiInstance.uiBetaOptInApi.fetchOptIn()
			.then(optIn => {
				if (optIn === null) {
					setIsPromptOpen(true);
				} else if (optIn) {
					window.location.href = newViewUrl;
				}
			})
			.catch(() => {
				// A failed fetchOptIn (or an unexpected error elsewhere in the chain, e.g.
				// sessionStorage being unavailable) is intentionally a no-op here: V1 is already
				// the default landing page during the rollout, so doing nothing just means the
				// prompt won't show this one time -- it'll show again on the next successful fetch.
			});
	}, [isManualView, newViewUrl]);

	const handleDecision = (optIn: boolean) => {
		setIsSaving(true);
		apiInstance.uiBetaOptInApi.putOptIn(optIn)
			.then(() => {
				if (optIn) {
					window.location.href = newViewUrl;
				}
			})
			.catch(() => {
				// Failing to save the decision shouldn't leave the user stuck behind the modal.
			})
			.finally(() => {
				setIsPromptOpen(false);
				setIsSaving(false);
			});
	};

	return (
		<ModalWithTitleAndButtons
			isOpen={isPromptOpen}
			onClose={() => {
				if (isSaving) return;
				handleDecision(false);
			}}
			title={t("betaOptIn.title")}
			modalButtons={(<>
				<Button label={t("betaOptIn.declineButtonLabel")}
						variant={"optional"}
						disabled={isSaving}
						onClick={() => handleDecision(false)}/>
				<Button label={t("betaOptIn.acceptButtonLabel")}
						variant={"primary"}
						disabled={isSaving}
						onClick={() => handleDecision(true)}/>
			</>)}
		>
			<div>{t("betaOptIn.message")}</div>
		</ModalWithTitleAndButtons>
	);
};

export default BetaOptInGuard;
